import React, { PropsWithChildren } from 'react';

import { FieldArrayWithId } from 'react-hook-form';

import { CostCenterDetailFlag } from '../../../../types';

import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import { useFieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';

import useFindIndexFromFieldsArray from 'components/shared/CRUD/hooks/useFindIndexFromFieldsArray';
import {
  BenefitStartDateField,
  BUHBlockedField,
  CodeExtField,
  CodeExtSecondaryField,
  CodeField,
  CodeImpField,
  CodeImpSecondaryField,
  EmployerField,
  FinancialCostCenterField,
  InvoiceCreditBlockedField,
  InvoiceDebitBlockedField,
  NameField,
  NameShortField,
  ParentCostCenterField,
  PlanningExportField,
  ValidFromField,
  ValidToField,
  VATDeductibleField
} from '../../fields/SubEntityFields';
import Grid from '@mui/material/Grid';

interface ExtendedFieldArrayWithId extends FieldArrayWithId {
  brDynId?: number;
}

const getLastAddedFieldId = (fields: FieldArrayWithId[]) =>
  fields.slice(-1)[0]?.id;

const FullWidthBox: React.FC<PropsWithChildren<any>> = ({ children }) => {
  return (
    <Grid item xs={12}>
      {children}
    </Grid>
  );
};

const HalfWidthBox: React.FC<PropsWithChildren<any>> = ({ children }) => {
  return (
    <Grid item xs={6}>
      {children}
    </Grid>
  );
};

const EditCostCenterDetail: React.FC<{ rowId: number }> = ({ rowId }) => {
  const { fields }: { fields: ExtendedFieldArrayWithId[] } =
    useFieldArrayContext();
  const fieldIndex = useFindIndexFromFieldsArray(
    rowId ?? getLastAddedFieldId(fields),
    fields
  );

  const selectedBrDynId = fields && fields[fieldIndex]?.brDynId;

  return (
    <Grid container mb={2.5} rowSpacing={2} columnSpacing={2}>
      <FullWidthBox>
        <CodeField name={`costCenterDetails[${fieldIndex}].code`} />
      </FullWidthBox>

      <FullWidthBox>
        <NameField name={`costCenterDetails[${fieldIndex}].name`} />
      </FullWidthBox>

      <FullWidthBox>
        <NameShortField name={`costCenterDetails[${fieldIndex}].nameShort`} />
      </FullWidthBox>

      <HalfWidthBox>
        <ValidFromField name={`costCenterDetails[${fieldIndex}].valid.from`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <ValidToField name={`costCenterDetails[${fieldIndex}].valid.to`} />
      </HalfWidthBox>

      <FullWidthBox>
        <EmployerField name={`costCenterDetails[${fieldIndex}].employerId`} />
      </FullWidthBox>

      <HalfWidthBox>
        <CodeExtField name={`costCenterDetails[${fieldIndex}].codeExt`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeExtSecondaryField
          name={`costCenterDetails[${fieldIndex}].codeExtSecondary`}
        />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeImpField name={`costCenterDetails[${fieldIndex}].codeImp`} />
      </HalfWidthBox>

      <HalfWidthBox>
        <CodeImpSecondaryField
          name={`costCenterDetails[${fieldIndex}].codeImpSecondary`}
        />
      </HalfWidthBox>

      <FullWidthBox>
        <BenefitStartDateField
          name={`costCenterDetails[${fieldIndex}].benefitStartDate`}
        />
      </FullWidthBox>

      <FullWidthBox>
        <FinancialCostCenterField
          name={`costCenterDetails[${fieldIndex}].finBrDynId`}
        />
      </FullWidthBox>

      <FullWidthBox>
        <ParentCostCenterField
          name={`costCenterDetails[${fieldIndex}].parentBrDynId`}
          selectedBrDynId={selectedBrDynId}
        />
      </FullWidthBox>

      <FullWidthBox>
        <BUHBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.BUH_BLOCKED}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <PlanningExportField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.PLANNING_EXPORT}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <VATDeductibleField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.VAT_DEDUCTIBLE}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <InvoiceDebitBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.INVOICE_DEBIT_BLOCKED}]`}
        />
      </FullWidthBox>
      <FullWidthBox>
        <InvoiceCreditBlockedField
          name={`costCenterDetails[${fieldIndex}].flags[${CostCenterDetailFlag.INVOICE_CREDIT_BLOCKED}]`}
        />
      </FullWidthBox>
    </Grid>
  );
};

export default withFieldArrayDrawer(EditCostCenterDetail, 'costCenterDetails');
